// DataTable — 21st.dev-inspired Tabellen-Komponente mit:
//   - Spalten-Toggle (zeigen/verstecken)
//   - Row-Stagger-Animation (.data-row CSS klasse)
//   - Empty-State + Loading-State
//   - Footer mit Pagination + „X von Y ausgewählt"-Text
//
// Avatar-Stack, Quality-Badge + External-Link sind separate Sub-Komponenten,
// die der Caller in Cell-Renderern verwendet.

import { ChevronDown, Columns3, ExternalLink, Filter, Search } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { qualityClass, type WalletQuality } from '@/lib/hl-quality';

export interface DataTableColumn<T> {
  /** Eindeutiger Key — wird für Spalten-Toggle + React-Key benutzt. */
  key: string;
  header: ReactNode;
  /** Renderer für eine Zelle. Bekommt Row-Daten. */
  cell: (row: T) => ReactNode;
  /** Spalten-Ausrichtung. Default 'left'. */
  align?: 'left' | 'right' | 'center';
  /** Zusätzliche <th>-Klassen. */
  thClassName?: string;
  /** Zusätzliche <td>-Klassen pro Zelle. */
  tdClassName?: string;
  /** Wenn true: User kann diese Spalte via Toggle ausblenden. Default true. */
  toggleable?: boolean;
}

export interface DataTableProps<T> {
  rows: ReadonlyArray<T>;
  columns: ReadonlyArray<DataTableColumn<T>>;
  /** Stabiler Key pro Row (z.B. row.id). */
  rowKey: (row: T) => string;
  /** Header über der Tabelle: Titel + Sub-Line + Custom-Right-Slot. */
  title?: ReactNode;
  subtitle?: ReactNode;
  /** Toolbar-Inhalt rechts (z.B. „Scan starten"-Button). */
  toolbar?: ReactNode;
  /** Search-Box anzeigen + Wert + Setter. */
  search?: {
    value: string;
    onChange: (next: string) => void;
    placeholder?: string;
  };
  /** Filter-Knopf anzeigen + onClick. */
  onFilterClick?: () => void;
  /** Spalten-Toggle aktivieren. Default true (wenn toggleable cols vorhanden). */
  enableColumnToggle?: boolean;
  /** Footer-Inhalt (Pagination etc.). */
  footer?: ReactNode;
  /** Loading-State. */
  loading?: boolean;
  /** Custom Empty-State. */
  emptyState?: ReactNode;
}

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  title,
  subtitle,
  toolbar,
  search,
  onFilterClick,
  enableColumnToggle = true,
  footer,
  loading = false,
  emptyState,
}: DataTableProps<T>) {
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(new Set());
  const [colMenuOpen, setColMenuOpen] = useState(false);

  const visibleColumns = columns.filter((c) => !hiddenCols.has(c.key));
  const hasToggleable = columns.some((c) => c.toggleable !== false);

  return (
    <div className="glass-strong overflow-hidden rounded-2xl">
      {/* Header */}
      {(title || subtitle || search || toolbar || (enableColumnToggle && hasToggleable)) ? (
        <div className="flex flex-wrap items-center gap-3 border-b border-white/5 px-4 py-3">
          {title || subtitle ? (
            <div>
              {title ? <h2 className="text-base font-semibold">{title}</h2> : null}
              {subtitle ? <p className="text-[11px] text-neutral-500">{subtitle}</p> : null}
            </div>
          ) : null}
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            {search ? (
              <div className="glass flex items-center gap-1.5 rounded-md px-2 py-1">
                <Search className="size-3 text-neutral-500" />
                <input
                  value={search.value}
                  onChange={(e) => search.onChange(e.target.value)}
                  placeholder={search.placeholder ?? 'Suchen...'}
                  className="w-32 bg-transparent text-[12px] placeholder:text-neutral-600 focus:outline-none"
                />
              </div>
            ) : null}
            {onFilterClick ? (
              <button
                type="button"
                onClick={onFilterClick}
                className="glass flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] text-neutral-300 hover:text-white"
              >
                <Filter className="size-3" />
                Filter
              </button>
            ) : null}
            {enableColumnToggle && hasToggleable ? (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setColMenuOpen((o) => !o)}
                  className="glass flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] text-neutral-300 hover:text-white"
                >
                  <Columns3 className="size-3" />
                  Spalten
                  <ChevronDown className="size-3" />
                </button>
                {colMenuOpen ? (
                  <div className="glass-strong absolute right-0 top-full z-10 mt-1 min-w-[160px] rounded-lg p-2 text-[12px]">
                    {columns
                      .filter((c) => c.toggleable !== false)
                      .map((c) => {
                        const hidden = hiddenCols.has(c.key);
                        return (
                          <label
                            key={c.key}
                            className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-white/5"
                          >
                            <input
                              type="checkbox"
                              checked={!hidden}
                              onChange={() => {
                                setHiddenCols((prev) => {
                                  const next = new Set(prev);
                                  if (hidden) next.delete(c.key);
                                  else next.add(c.key);
                                  return next;
                                });
                              }}
                            />
                            <span className="truncate">{c.header}</span>
                          </label>
                        );
                      })}
                  </div>
                ) : null}
              </div>
            ) : null}
            {toolbar}
          </div>
        </div>
      ) : null}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.02] text-left text-[10px] uppercase tracking-wider text-neutral-500">
            <tr>
              {visibleColumns.map((c) => (
                <th
                  key={c.key}
                  className={`px-2 py-2 font-medium ${alignClass(c.align)} ${c.thClassName ?? ''}`}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-[13px] tabular">
            {loading ? (
              <tr>
                <td colSpan={visibleColumns.length} className="px-4 py-6 text-center text-neutral-500">
                  Lädt...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length} className="px-4 py-8 text-center text-neutral-500">
                  {emptyState ?? 'Keine Einträge.'}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={rowKey(row)}
                  className="data-row border-t border-white/5 hover:bg-white/[0.03]"
                >
                  {visibleColumns.map((c) => (
                    <td
                      key={c.key}
                      className={`px-2 py-2 ${alignClass(c.align)} ${c.tdClassName ?? ''}`}
                    >
                      {c.cell(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      {footer ? <div className="border-t border-white/5 px-4 py-2 text-[11px] text-neutral-500">{footer}</div> : null}
    </div>
  );
}

function alignClass(align: 'left' | 'right' | 'center' | undefined): string {
  if (align === 'right') return 'text-right';
  if (align === 'center') return 'text-center';
  return 'text-left';
}

// ── Sub-Components für Cell-Renderer ─────────────────────────────────────────

export function QualityBadge({ quality }: { quality: WalletQuality }) {
  return <span className={qualityClass(quality)}>{quality}</span>;
}

export interface AvatarSpec {
  /** Kurz-Label (2-3 chars). */
  initials: string;
  /** Bg-Tone für den runden Hintergrund. */
  tone?: 'amber' | 'emerald' | 'rose' | 'neutral';
}

interface AvatarStackProps {
  avatars: ReadonlyArray<AvatarSpec>;
  /** Optional max sichtbare — Rest wird als „+N" angehängt. */
  max?: number;
}

export function AvatarStack({ avatars, max = 3 }: AvatarStackProps) {
  const visible = avatars.slice(0, max);
  const overflow = avatars.length - visible.length;
  return (
    <div className="avatar-stack flex">
      {visible.map((a, i) => (
        <div
          key={i}
          className={`grid size-6 place-items-center rounded-full text-[10px] font-bold ring-2 ring-neutral-950 ${avatarToneClass(a.tone)}`}
        >
          {a.initials}
        </div>
      ))}
      {overflow > 0 ? (
        <div className="grid size-6 place-items-center rounded-full bg-neutral-700 text-[10px] font-bold text-neutral-300 ring-2 ring-neutral-950">
          +{overflow}
        </div>
      ) : null}
    </div>
  );
}

function avatarToneClass(tone: AvatarSpec['tone']): string {
  if (tone === 'amber') return 'bg-amber-500/20 text-amber-300';
  if (tone === 'emerald') return 'bg-emerald-500/20 text-emerald-300';
  if (tone === 'rose') return 'bg-rose-500/20 text-rose-300';
  return 'bg-neutral-700 text-neutral-300';
}

export function ExternalLinkButton({ href, label }: { href: string; label?: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label ?? 'External link'}
      className="text-neutral-600 hover:text-neutral-300"
    >
      <ExternalLink className="size-2.5 inline" />
    </a>
  );
}
